import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { PipelineTemplateView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { Chip } from '../../../../components/Chip';
import { Field } from '../../../../components/Field';
import { Input } from '../../../../components/Input';
import { ApiError, errorMessage } from '../../../../lib/api-client';
import { useStageLabels } from '../../../../lib/modules';
import { useToasts } from '../../../../stores/toasts';
import { useSavePipelineTemplate } from '../../api/jobs-api';
import { StageBuilder } from '../StageBuilder';
import { useLeaveGuard } from './LeaveGuard';
import { familyClashOf, isSameDraft, pipelineBodyOf, pipelineDraftOf, pipelineProblemOf, type PipelineDraft } from './pipeline-draft';
import { useServerDraft } from './use-server-draft';
import styles from './AdminSetup.module.css';

/** The parts of a pipeline this editor edits: a new version that changed none of them leaves the draft alone. */
function isSameEditable(left: PipelineTemplateView, right: PipelineTemplateView): boolean {
  return isSameDraft(pipelineDraftOf(left), pipelineDraftOf(right));
}

export function openJobsText(count: number): string {
  if (count === 0) return 'No open job started from it yet.';
  return `${count} open ${count === 1 ? 'job' : 'jobs'} started from it. Each keeps its own copy, so saving here changes none of them.`;
}

interface PipelineEditorProps {
  /** The pipeline as the server has it now; null for a new one. */
  readonly latest: PipelineTemplateView | null;
  readonly pipelines: readonly PipelineTemplateView[];
  readonly canEdit: boolean;
  readonly onCreated: (id: string) => void;
  readonly onCancelNew: () => void;
  /** After a refused save, fetch the list as it is now. */
  readonly onRefetch: () => void;
}

/**
 * ADMIN-01 (Pipelines A): one pipeline, beside the list. Name, role family and default, then the stages as one table and
 * the names for leaving the route. Save stays a button: a pipeline is set up once, and every new job copies it.
 */
export function PipelineEditor({ latest, pipelines, canEdit, onCreated, onCancelNew, onRefetch }: PipelineEditorProps): ReactNode {
  const stageLabels = useStageLabels();
  const save = useSavePipelineTemplate();
  const show = useToasts((state) => state.show);
  const isFirst = pipelines.length === 0;
  const editor = useServerDraft<PipelineTemplateView, PipelineDraft>({
    latest,
    draftOf: (view) => pipelineDraftOf(view, isFirst),
    isSameDraft,
    isSameEditable,
  });
  const { draft, base, isStale } = editor;
  const isNew = base === null;
  const isDirty = editor.isDirty;
  const headRef = useRef<HTMLElement | null>(null);
  const { guard, release } = useLeaveGuard(canEdit && isDirty, headRef);
  const [familyError, setFamilyError] = useState<string | null>(null);

  const change = (patch: Partial<PipelineDraft>): void => {
    editor.setDraft((current) => ({ ...current, ...patch }));
    if (patch.roleFamily !== undefined) setFamilyError(null);
  };

  const clash = familyClashOf(draft, pipelines, base?.id ?? null);
  const problem = pipelineProblemOf(draft);
  const problemText = problem?.stage ? `${stageLabels[problem.stage]}: ${problem.message}` : (problem?.message ?? null);
  const shownFamilyError = familyError ?? (clash ? `“${clash.name}” already covers this role family. Each role family has one pipeline.` : null);
  const canSave = canEdit && (isNew || isDirty) && problem === null && clash === null && !isStale;

  async function onSave(): Promise<void> {
    if (!canSave || save.isPending) return;
    const sent = draft;
    const known = new Set(pipelines.map((pipeline) => pipeline.id));
    try {
      const rows = await save.mutateAsync({ id: base?.id ?? null, version: base?.version ?? 0, body: pipelineBodyOf(sent) });
      show({ tone: 'success', message: `${sent.name.trim()} saved`, detail: 'New jobs copy it from now on. Jobs already made keep theirs.' });
      if (base === null) {
        const created = rows.find((row) => !known.has(row.id));
        release();
        if (created) onCreated(created.id);
        return;
      }
      const saved = rows.find((row) => row.id === base.id);
      if (saved) editor.adopt(saved, sent);
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields.roleFamily) setFamilyError(cause.fields.roleFamily);
      if (cause instanceof ApiError && cause.status === 409) onRefetch();
      show({ tone: 'danger', message: 'The pipeline was not saved', detail: errorMessage(cause) });
    }
  }

  const cancel = (): void => {
    if (isNew) {
      release();
      onCancelNew();
      return;
    }
    editor.discard();
    setFamilyError(null);
  };

  // 06-qol §2: every form submits on Ctrl/Cmd+Enter.
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    void onSave();
  };

  const title = isNew ? 'New pipeline' : (base?.name ?? '');

  return (
    <section className={styles.editor} aria-label={isNew ? 'New pipeline' : `Pipeline ${title}`} onKeyDown={onKeyDown}>
      <header ref={headRef} className={styles.editorHead}>
        <h2 className={styles.editorTitle}>{title}</h2>
        {isDirty && canEdit && (
          <Chip tone="attention" plain>
            Unsaved changes
          </Chip>
        )}
        {canEdit && (
          <span className={styles.editorActions}>
            {(isDirty || isNew) && (
              <Button variant="ghost" size="sm" onClick={cancel}>
                Cancel
              </Button>
            )}
            <Button variant="solid" size="sm" disabled={!canSave} isPending={save.isPending} onClick={() => void onSave()}>
              Save pipeline
            </Button>
          </span>
        )}
      </header>

      {canEdit && isDirty && problemText && (
        <p className={styles.problem} role="status">
          {problemText}
        </p>
      )}
      {isStale && (
        <div className={styles.stale} role="alert">
          <span>Someone else saved this pipeline while you were editing. Your changes here can't be saved over theirs.</span>
          <Button variant="outline" size="sm" onClick={editor.reload}>
            Reload theirs
          </Button>
        </div>
      )}

      <div className={styles.editorBody}>
        <div className={styles.fieldRow}>
          <Field label="Name" isRequired>
            {({ inputId }) => (
              <Input id={inputId} disabled={!canEdit} value={draft.name} placeholder="Engineering" maxLength={120} onChange={(event) => change({ name: event.target.value })} />
            )}
          </Field>
          <Field label="Role family" isOptional hint="A label to group it by. Role templates can start from this pipeline; a job without a template starts from the default." error={shownFamilyError ?? undefined}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                isInvalid={isInvalid}
                disabled={!canEdit}
                value={draft.roleFamily}
                placeholder="Engineering"
                maxLength={80}
                onChange={(event) => change({ roleFamily: event.target.value })}
              />
            )}
          </Field>
        </div>
        <Checkbox
          checked={draft.isDefault}
          disabled={!canEdit}
          label="Use for any job made without a role template"
          onChange={(isDefault) => change({ isDefault })}
        />
        {latest && <p className={styles.note}>{openJobsText(latest.openJobCount)}</p>}

        <StageBuilder layout="table" value={draft.pipeline} isReadOnly={!canEdit} onChange={(pipeline) => change({ pipeline })} />
      </div>
      {guard}
    </section>
  );
}
